import { useState, useEffect, useMemo, useRef } from "react";
import ProgressBar from "../../../CommonComponents/Progressbar";
import BatchOverview from "./BatchOverview";
import InputDropdown from "../../../CommonComponents/InputDropdown";
import Table from "../../../CommonComponents/Table";
import Search from "../../../CommonComponents/Search";
import useAxiosPrivate from "../../../../Hooks/useAxiosPrivate";
import Loading from "../../../Notification-Loading/Loading/Loading";
import contants from "../../../../constants";
import LeaderboardSkeleton from "./leaderboardSkeleton";
import TopStudentsLeaderSkeleton from "./TopStudentsLeaderSkeleton";
import Skeleton from "../../../CommonComponents/Skeleton";
import Tooltip from "../../../CommonComponents/Tooltip";
import IconButton from "../../../CommonComponents/IconButton";
import { useContext } from "react";
import AlertContext from "../../../../context/AlertContext";
import {
  Typography,
} from "@mui/material";

const getRankBadgeStyle = (rank) => {
  switch (rank) {
    case 1:
      return { backgroundColor: "#FEF9C2", textColor: "#A65F00" };
    case 2:
      return { backgroundColor: "#E2E8F0", textColor: "#314158" };
    case 3:
      return { backgroundColor: "#FEF3C6", textColor: "#BB4D00" };
    default:
      return { backgroundColor: "#F1F5F9", textColor: "#475569" };
  }
};

// Helper: leaderboard rank badge (solid colors, white text)
const getLeaderboardRankStyle = (rank) => {
  switch (rank) {
    case 1:
      return { backgroundColor: "#FDC700", color: "#FFFFFF" };
    case 2:
      return { backgroundColor: "#90A1B9", color: "#FFFFFF" };
    case 3:
      return { backgroundColor: "#E17100", color: "#FFFFFF" };
    default:
      return { backgroundColor: "#E2E8F0", color: "#475569" };
  }
};

function StatCard({
  label,
  value,
  change,
  icon,
  changeColor = "#008236",
  changeBg = "#F0FDF4",
  loading = false,
}) {
  const isNegative = change && change.startsWith("-");
  const arrow = isNegative ? "↓" : "↑";
  const formattedChange = change ? change.replace("-", "") : null;

  return (
    <div className="StatCard">
      <div className="StatCardHeader">
        <span className="StatLabel">{label}</span>
        <div className="TopStatsIcon">{icon}</div>
      </div>
      <div className="StatCardValueRow">
        {loading ? (
          <Skeleton variant="rounded" width={80} height={32} />
        ) : (
          <span className="StatValue">{value}</span>
        )}
        {change &&
          (loading ? (
            <Skeleton variant="rounded" width={50} height={24} />
          ) : (
            <div className="StatChange" style={{ backgroundColor: changeBg }}>
              <span className="StatChangeArrow" style={{ color: changeColor }}>
                {arrow}
              </span>
              <span className="StatChangeValue" style={{ color: changeColor }}>
                {formattedChange}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

function SectionCard({ icon, iconBg = "#FCEBE8", title, children, headerRight }) {
  return (
    <div className="section-card">
      <div className="section-card-header">
        <div className="section-card-header-left">
          <div className="section-card-icon" style={{ backgroundColor: iconBg }}>
            {icon}
          </div>
          <h2 className="section-card-title">{title}</h2>
        </div>
        {headerRight && <div className="section-card-header-right">{headerRight}</div>}
      </div>
      {children}
    </div>
  );
}

const RefreshIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.6665 8.33333C1.6665 8.33333 3.33732 6.05685 4.6947 4.69854C6.05208 3.34022 7.92783 2.5 9.99984 2.5C14.142 2.5 17.4998 5.85786 17.4998 10C17.4998 14.1421 14.142 17.5 9.99984 17.5C6.58059 17.5 3.69576 15.2119 2.79298 12.0833M1.6665 8.33333V3.33333M1.6665 8.33333H6.6665"
      stroke="#667085"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function UniversityDashboard() {
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [batchFilter, setBatchFilter] = useState("All Batches");
  const [batchSearch, setBatchSearch] = useState("");

  const { setShowNotify } = useContext(AlertContext)

  const [dashboardStats, setDashboardStats] = useState({
    totalLearners: 0,
    activeLearners: { thisWeek: 0, growth: 0 },
    assignmentCompletion: { value: 0, growth: 0 },
  });

  const [codingMetrics, setCodingMetrics] = useState({
    activeUsers: { thisWeek: 0, growth: 0 },
    avgProblemsSolved: 0,
    totalSubmissions: 0,
    accuracyRate: 0,
    participationRate: 0,
  });
  const [codingLoading, setCodingLoading] = useState(true);

  const [mockMetrics, setMockMetrics] = useState({
    activeUsers: 0,
    avgTestsPerStudent: 0,
    totalAttempts: 0,
    topUser: { name: "", testsCompleted: 0, totalScore: 0 },
    totalTests: 0,
    avgScore: 0,
    totalTestTaken: 0,
  });
  const [mockLoading, setMockLoading] = useState(true);

  const [ccPerformers, setCcPerformers] = useState([]);
  const [performersLoading, setPerformersLoading] = useState(true);

  const [topStudents, setTopStudents] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);

  const [loading, setLoading] = useState(true);

  const [batches, setbatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(true);

  const [isRefresh, setIsRefresh] = useState(false);
  const isRefreshRef = useRef(false);
  // const resetBatchRef = useRef(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [cached,setIsCached] = useState(null);


  const axios = useAxiosPrivate();
  const axiosRef = useRef(axios);

  useEffect(() => {
    setLoading(true);
    setCodingLoading(true);
    setMockLoading(true);
    setPerformersLoading(true);

    axios
      .get("/node/account/dashboard/stats")
      .then((res) => {
        const data = res.data;
        setDashboardStats({
          totalLearners: data.totalLearners || 0,
          activeLearners: {
            thisWeek: data.thisWeekActiveLearners || 0,
            growth: data.growthPercentage || 0,
          },
          assignmentCompletion: {
            value: data.assignmentCompletion?.overallCompletionPercentage || 0,
            growth: data.assignmentCompletion?.growthPercentage || 0,
          },
        });
      })
      .catch((err) => console.error("Stats error:", err))
      .finally(() => setLoading(false));

    // Coding Metrics
    axios
      .get("/node/account/dashboard/cc/metrics")
      .then((res) => {
        const data = res.data;
        if (data.resultCode === contants.RESULT_STATUS.SUCCESS) {
          setCodingMetrics({
            activeUsers: {
              thisWeek: data.metrics.activeUsers.thisWeek || 0,
              growth: data.metrics.activeUsers.growth || 0,
            },
            avgProblemsSolved: data.metrics.avgProblemsSolved || 0,
            totalSubmissions: data.metrics.totalSubmissions || 0,
            accuracyRate: data.metrics.accuracyRate || 0,
            participationRate: data.metrics.participationRate || 0,
          });
        }
      })
      .catch((err) => console.error("Coding error:", err))
      .finally(() => setCodingLoading(false));

    // Mock Metrics
    axios
      .get("/node/account/dashboard/mock")
      .then((res) => {
        const data = res.data;
        if (data.resultCode === contants.RESULT_STATUS.SUCCESS) {
          setMockMetrics({
            activeUsers: data.metrics.activeUsers || 0,
            avgTestsPerStudent: data.metrics.avgTestsPerStudent || 0,
            totalAttempts: data.metrics.totalAttempts || 0,
            topUser: {
              name: data.metrics.topUser?.name || "",
              testsCompleted: data.metrics.topUser?.testsCompleted || 0,
              totalScore: data.metrics.topUser?.totalScore || 0,
            },
            totalTests: data.metrics.totalTests || 0,
            avgScore: data.metrics.avgScore || 0,
            totalTestTaken: data.metrics.totalTestTaken || 0,
          });
        }
      })
      .catch((err) => console.error("Mock error:", err))
      .finally(() => setMockLoading(false));

    // Performers
    axios
      .get("/node/account/dashboard/cc/performers")
      .then((res) => {
        const data = res.data;
        if (data.resultCode === contants.RESULT_STATUS.SUCCESS) {
          setCcPerformers(data.performers);
        }
      })
      .catch((err) => console.error("Performers error:", err))
      .finally(() => setPerformersLoading(false));


    axios
      .get("/node/account/dashboard/batch")
      .then((res) => {
        const data = res.data;
        setbatches(data); // array of { id, company_id, name }
      })
      .catch((err) => console.error("Batches error:", err))
      .finally(() => setBatchesLoading(false));
  }, []);

  useEffect(() => {
    setLeaderboardLoading(true);

    const url =
      batchFilter === "All Batches"
        ? "/node/account/dashboard/topstudents"
        : `/node/account/dashboard/topstudents?batchid=${batchFilter}`;

    axios
      .get(url)
      .then((res) => {
        const data = res.data;
        if (data?.message === "Endpoint request timed out") {
          setShowNotify({
            show: true,
            title: "Info",
            msg: "Data will take some time to load check after some time",
          });
        }
        if (data.resultCode === contants.RESULT_STATUS.SUCCESS) {
          const formatted = data.performers.map((student, index) => ({
            rank: student.rank,
            name: student.name,
            batch: student.batchName || "-",
            score: student.averageScore || student.score || 0,
            completion: student.assignmentCompleted || 0,
            initials: () => {
              const parts = student.name.split(" ");
              return [parts[0], parts[parts.length - 1]].map(n => n[0]).join("");
            }
          }));
          setTopStudents(formatted);
        }
      })
      .catch((err) => {
        console.error("Leaderboard error:", err)
        if (err.response?.status === 504) {
          setShowNotify({
            show: true,
            title: "Info",
            msg: "Data will take some time to load. Check again later."
          });
        }
      })
      .finally(() => setLeaderboardLoading(false));

  }, [batchFilter]);

  const batchOptions = useMemo(
    () => [
      { label: "All Batches", value: "All Batches" },
      ...batches.map((b) => ({ label: b.name, value: b.id })),
    ],
    [batches]
  );

  // const filteredData =
  //   batchFilter === "All Batches"
  //     ? topStudents
  //     : topStudents.filter((s) => s.batch === batchFilter);

  const columns = [
    {
      key: "rank",
      title: "RANK",
      render: (value) => {
        const style = getRankBadgeStyle(value);
        return (
          <div
            className="rank-badge"
            style={{
              backgroundColor: style.backgroundColor,
              color: style.textColor,
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      key: "name",
      title: "STUDENT NAME",
      render: (value, row) => {
        const defaultAvatarStyle = { backgroundColor: "#E2E8F0", color: "#475569" };
        return (
          <div className="student-cell">
            <div className="student-avatar" style={defaultAvatarStyle}>
              {row.avatarUrl ? (
                <img src={row.avatarUrl} alt={value} className="avatar-image" />
              ) : (
                row.initials()
              )}
            </div>
            <span>{value}</span>
          </div>
        );
      },
    },
    {
      key: "batch",
      title: "BATCH",
      render: (value) => (value ?? []).join(", ")
    },
    {
      key: "score",
      title: "AVG SCORE",
      render: (value) => (value ?? 0).toLocaleString(),
    },
    {
      key: "completion",
      title: "ASSIGNMENT COMPLETION",
      render: (value) => (value ?? 0).toLocaleString()
    },
  ];

  return (
    <div className="UniversityDashboard">
      <div className="dashboard-main">
        {/* Tab bar with inline search */}
        <div className="DashboardSwitchTabs">
          <div className="tabs-group">
            <div
              onClick={() => {
                setActiveTab("Dashboard");
                isRefreshRef.current = false;
              }}
              className={`DashboardTab ${activeTab === "Dashboard" ? "DashboardTabActive" : ""}`}
            >
              Dashboard
            </div>
            <div
              onClick={() => {
                setActiveTab("Batch Overview");
                isRefreshRef.current = false;

              }}
              className={`DashboardTab ${activeTab === "Batch Overview" ? "DashboardTabActive" : ""}`}
            >
              Batch Overview
            </div>
          </div>
          {activeTab === "Batch Overview" && (
            <div className="batch-search-wrapper">
              {cached && (
                <Typography className="batch-overview-cached">
                  Updated at {cached}
                </Typography>
              )}
              <Tooltip placement="bottom" title="Refresh" isIcon={true}>
                <IconButton
                  hierarchy="tertiaryGrey"
                  onClick={() => {
                    isRefreshRef.current = true;
                    setRefreshTrigger(prev => prev + 1);
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Search
                value={batchSearch}
                onChange={(e) => setBatchSearch(e.target.value)}
                placeholder="Search batches..."
                width="250px"
                height="36px"
              />
            </div>
          )}
        </div>

        {activeTab === "Dashboard" ? (
          <div className="dashboard-content">
            {/* Top Stats Row */}
            <div className="DashboardTopStats">
              <StatCard
                label="Total Learners"
                value={loading ? null : dashboardStats.totalLearners.toLocaleString()}
                change={null}
                loading={loading}
                icon={
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                    <path d="M0 10C0 4.47715 4.47715 0 10 0H24C29.5228 0 34 4.47715 34 10V24C34 29.5228 29.5228 34 24 34H10C4.47715 34 0 29.5228 0 24V10Z" fill="#FCEBE8" />
                    <path d="M20 23.75V22.25C20 21.4544 19.6839 20.6913 19.1213 20.1287C18.5587 19.5661 17.7956 19.25 17 19.25H12.5C11.7044 19.25 10.9413 19.5661 10.3787 20.1287C9.81607 20.6913 9.5 21.4544 9.5 22.25V23.75" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14.75 16.25C16.4069 16.25 17.75 14.9069 17.75 13.25C17.75 11.5931 16.4069 10.25 14.75 10.25C13.0931 10.25 11.75 11.5931 11.75 13.25C11.75 14.9069 13.0931 16.25 14.75 16.25Z" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M24.5 23.7502V22.2502C24.4995 21.5855 24.2783 20.9397 23.871 20.4144C23.4638 19.889 22.8936 19.5138 22.25 19.3477" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 10.3477C20.6453 10.5129 21.2173 10.8882 21.6257 11.4144C22.0342 11.9406 22.2559 12.5878 22.2559 13.2539C22.2559 13.92 22.0342 14.5672 21.6257 15.0934C21.2173 15.6196 20.6453 15.9949 20 16.1602" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              />
              <StatCard
                label="Active Learners"
                value={loading ? null : dashboardStats.activeLearners.thisWeek}
                change={loading ? " " : `${dashboardStats.activeLearners.growth}%`}
                changeColor={dashboardStats.activeLearners.growth >= 0 ? "#008236" : "#DC2626"}
                changeBg={dashboardStats.activeLearners.growth >= 0 ? "#F0FDF4" : "#FEF2F2"}
                loading={loading}
                icon={
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                    <path d="M0 10C0 4.47715 4.47715 0 10 0H24C29.5228 0 34 4.47715 34 10V24C34 29.5228 29.5228 34 24 34H10C4.47715 34 0 29.5228 0 24V10Z" fill="#FCEBE8" />
                    <path d="M24.3508 15.5001C24.6933 17.1811 24.4492 18.9287 23.6592 20.4515C22.8691 21.9742 21.5809 23.1802 20.0094 23.8681C18.4379 24.5561 16.678 24.6845 15.0232 24.2319C13.3685 23.7793 11.9189 22.7732 10.9162 21.3812C9.91353 19.9892 9.41836 18.2955 9.51326 16.5827C9.60817 14.8698 10.2874 13.2412 11.4378 11.9685C12.5881 10.6958 14.1399 9.85594 15.8346 9.58895C17.5292 9.32197 19.2641 9.64401 20.75 10.5014" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14.75 16.25L17 18.5L24.5 11" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              />
              <StatCard
                label="Assignment Completion"
                value={loading ? null : `${dashboardStats.assignmentCompletion.value}%`}
                change={loading ? " " : `${dashboardStats.assignmentCompletion.growth}%`}
                changeColor={dashboardStats.assignmentCompletion.growth >= 0 ? "#008236" : "#DC2626"}
                changeBg={dashboardStats.assignmentCompletion.growth >= 0 ? "#F0FDF4" : "#FEF2F2"}
                loading={loading}
                icon={
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                    <path d="M0 10C0 4.47715 4.47715 0 10 0H24C29.5228 0 34 4.47715 34 10V24C34 29.5228 29.5228 34 24 34H10C4.47715 34 0 29.5228 0 24V10Z" fill="#FCEBE8" />
                    <g clipPath="url(#clip0_4575_4374)">
                      <path d="M17 13.25V23.75" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.25 21.5C10.0511 21.5 9.86032 21.421 9.71967 21.2803C9.57902 21.1397 9.5 20.9489 9.5 20.75V11C9.5 10.8011 9.57902 10.6103 9.71967 10.4697C9.86032 10.329 10.0511 10.25 10.25 10.25H14C14.7956 10.25 15.5587 10.5661 16.1213 11.1287C16.6839 11.6913 17 12.4544 17 13.25C17 12.4544 17.3161 11.6913 17.8787 11.1287C18.4413 10.5661 19.2044 10.25 20 10.25H23.75C23.9489 10.25 24.1397 10.329 24.2803 10.4697C24.421 10.6103 24.5 10.8011 24.5 11V20.75C24.5 20.9489 24.421 21.1397 24.2803 21.2803C24.1397 21.421 23.9489 21.5 23.75 21.5H19.25C18.6533 21.5 18.081 21.7371 17.659 22.159C17.2371 22.581 17 23.1533 17 23.75C17 23.1533 16.7629 22.581 16.341 22.159C15.919 21.7371 15.3467 21.5 14.75 21.5H10.25Z" stroke="#F96343" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_4575_4374">
                        <rect width="18" height="18" fill="white" transform="translate(8 8)" />
                      </clipPath>
                    </defs>
                  </svg>
                }
              />
            </div>

            {/* Coding Challenges Section */}
            <div className="coding-challenges-section">
              <SectionCard
                icon={
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <path d="M0 10C0 4.47715 4.47715 0 10 0H26C31.5228 0 36 4.47715 36 10V26C36 31.5228 31.5228 36 26 36H10C4.47715 36 0 31.5228 0 26V10Z" fill="#FCEBE8" />
                    <g clipPath="url(#clip0_4575_4392)">
                      <path d="M23 21.3332L26.3333 17.9998L23 14.6665" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M13 14.6665L9.66667 17.9998L13 21.3332" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M20.0833 11.3335L15.9167 24.6668" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_4575_4392">
                        <rect width="20" height="20" fill="white" transform="translate(8 8)" />
                      </clipPath>
                    </defs>
                  </svg>
                }
                title="Coding Challenges"
              >
                <div className="coding-challenges-content">
                  <div className="stats-column">
                    <div className="stats-grid">
                      {/* Active Users */}
                      <div className="stat-item">
                        <div className="stat-header">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M9.33334 12.25V11.0833C9.33334 10.4645 9.08751 9.871 8.64992 9.43342C8.21234 8.99583 7.61884 8.75 7 8.75H3.50001C2.88117 8.75 2.28767 8.99583 1.85009 9.43342C1.4125 9.871 1.16667 10.4645 1.16667 11.0833V12.25" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M5.25001 6.41667C6.53867 6.41667 7.58334 5.372 7.58334 4.08333C7.58334 2.79467 6.53867 1.75 5.25001 1.75C3.96134 1.75 2.91667 2.79467 2.91667 4.08333C2.91667 5.372 3.96134 6.41667 5.25001 6.41667Z" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12.8333 12.2499V11.0832C12.8329 10.5662 12.6609 10.064 12.3441 9.65537C12.0274 9.24677 11.5839 8.95493 11.0833 8.82568" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9.33333 1.82568C9.83524 1.95419 10.2801 2.24609 10.5978 2.65536C10.9155 3.06464 11.0879 3.568 11.0879 4.0861C11.0879 4.6042 10.9155 5.10756 10.5978 5.51684C10.2801 5.92611 9.83524 6.21801 9.33333 6.34652" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Active Users</span>
                        </div>
                        <div className="stat-number">
                          {codingLoading ? (
                            <Skeleton variant="rounded" width={60} height={20} />
                          ) : (
                            codingMetrics.activeUsers.thisWeek
                          )}
                        </div>
                        <div className="stat-trend positive">
                          {codingLoading ? (
                            <Skeleton variant="rounded" width={100} height={16} />
                          ) : (
                            `${codingMetrics.activeUsers.growth}% vs last week`
                          )}
                        </div>
                      </div>

                      {/* Avg Problems Solved */}
                      <div className="stat-item">
                        <div className="stat-header">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M12.7172 5.8332C12.9837 7.14063 12.7938 8.49987 12.1793 9.68425C11.5649 10.8686 10.5629 11.8066 9.34063 12.3416C8.11833 12.8767 6.74953 12.9766 5.4625 12.6246C4.17548 12.2726 3.04803 11.49 2.26816 10.4074C1.48829 9.32471 1.10315 8.00743 1.17697 6.67518C1.25078 5.34293 1.77909 4.07626 2.67379 3.08639C3.56849 2.09652 4.7755 1.44329 6.09354 1.23564C7.41157 1.02798 8.76095 1.27846 9.91666 1.94529" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M5.25 6.41683L7 8.16683L12.8333 2.3335" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Avg Problems Solved</span>
                        </div>
                        <div className="stat-number">
                          {codingLoading ? (
                            <Skeleton variant="rounded" width={60} height={20} />
                          ) : (
                            (() => {
                              const submissions = codingMetrics?.totalSubmissions || 0;
                              const activeUsers = codingMetrics?.activeUsers?.thisWeek || 0;

                              if (submissions === 0 || activeUsers === 0) {
                                return "0.00";
                              }

                              return (submissions / activeUsers).toFixed(2);
                            })()
                          )}
                        </div>
                        <div className="stat-sub">Per student</div>
                      </div>

                      {/* Total Submissions */}
                      <div className="stat-item">
                        <div className="stat-header">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M10.5 9.33317L12.8333 6.99984L10.5 4.6665" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3.50001 4.6665L1.16667 6.99984L3.50001 9.33317" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8.45834 2.3335L5.54167 11.6668" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Total Submissions</span>
                        </div>
                        <div className="stat-number">
                          {codingLoading ? (
                            <Skeleton variant="rounded" width={60} height={20} />
                          ) : (
                            codingMetrics.totalSubmissions
                          )}
                        </div>
                      </div>

                      {/* Accuracy Rate */}
                      <div className="stat-item">
                        <div className="stat-header">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M6.99999 12.8332C10.2217 12.8332 12.8333 10.2215 12.8333 6.99984C12.8333 3.77818 10.2217 1.1665 6.99999 1.1665C3.77833 1.1665 1.16666 3.77818 1.16666 6.99984C1.16666 10.2215 3.77833 12.8332 6.99999 12.8332Z" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12.8333 7H10.5" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3.49999 7H1.16666" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7 3.49984V1.1665" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7 12.8333V10.5" stroke="#62748E" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Accuracy Rate</span>
                        </div>
                        <div className="stat-number">
                          {codingLoading ? (
                            <Skeleton variant="rounded" width={60} height={20} />
                          ) : (
                            `${codingMetrics.accuracyRate}%`
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="participation-row">
                      <div className="participation-header">
                        <span>Participation Rate</span>
                        <span className="participation-value">
                          {codingLoading ? (
                            <Skeleton variant="rounded" width={40} height={20} />
                          ) : (
                            `${codingMetrics.participationRate}%`
                          )}
                        </span>
                      </div>
                      {codingLoading ? (
                        <Skeleton variant="rounded" width="100%" height={8} />
                      ) : (
                        <ProgressBar percentage={codingMetrics.participationRate} />
                      )}
                    </div>
                  </div>

                  <div className="leaderboard-column">
                    <div className="leaderboard-title">TOP PERFORMERS</div>
                    <div className="leaderboard-list">
                      {performersLoading ? (
                        <LeaderboardSkeleton />
                      ) : ccPerformers.length > 0 ? (
                        ccPerformers.map((performer) => {
                          const style = getLeaderboardRankStyle(performer.rank);
                          return (
                            <div key={performer.rank} className="leaderboard-item">
                              <div
                                className="leaderboard-rank"
                                style={{
                                  backgroundColor: style.backgroundColor,
                                  color: style.color,
                                }}
                              >
                                {performer.rank}
                              </div>
                              <span className="leaderboard-name">{performer.name}</span>
                              <div className="leaderboard-score">
                                <div className="score-label">Solved</div>
                                <div className="score-value">
                                  {performer.problemsSolved || performer.score || 0}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="leaderboard-item">No data available</div>
                      )}
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Mock Tests Section */}
            <div className="mock-tests-section">
              <SectionCard
                icon={
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <path d="M0 10C0 4.47715 4.47715 0 10 0H26C31.5228 0 36 4.47715 36 10V26C36 31.5228 31.5228 36 26 36H10C4.47715 36 0 31.5228 0 26V10Z" fill="#FCEBE8" />
                    <path d="M20.5 9.6665H13C12.558 9.6665 12.134 9.8421 11.8215 10.1547C11.5089 10.4672 11.3333 10.8911 11.3333 11.3332V24.6665C11.3333 25.1085 11.5089 25.5325 11.8215 25.845C12.134 26.1576 12.558 26.3332 13 26.3332H23C23.442 26.3332 23.8659 26.1576 24.1785 25.845C24.4911 25.5325 24.6667 25.1085 24.6667 24.6665V13.8332L20.5 9.6665Z" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19.6667 9.6665V12.9998C19.6667 13.4419 19.8423 13.8658 20.1548 14.1783C20.4674 14.4909 20.8913 14.6665 21.3333 14.6665H24.6667" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M16.3333 15.5H14.6667" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21.3333 18.8335H14.6667" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21.3333 22.1665H14.6667" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
                title="Mock Tests Analytics"
              >
                <div className="mock-tests-content">
                  <div className="mock-stats-row">
                    {/* Active Users */}
                    <div className="mock-stat">
                      <div className="mock-stat-label">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M10.6667 14V12.6667C10.6667 11.9594 10.3857 11.2811 9.88562 10.781C9.38553 10.281 8.70725 10 8 10H4C3.29276 10 2.61447 10.281 2.11438 10.781C1.61429 11.2811 1.33333 11.9594 1.33333 12.6667V14" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M6 7.33333C7.47276 7.33333 8.66667 6.13943 8.66667 4.66667C8.66667 3.19391 7.47276 2 6 2C4.52724 2 3.33333 3.19391 3.33333 4.66667C3.33333 6.13943 4.52724 7.33333 6 7.33333Z" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M14.6667 14V12.6667C14.6662 12.0907 14.4748 11.5312 14.124 11.0662C13.7731 10.6012 13.2827 10.258 12.6667 10.1021" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10.6667 2.10205C11.2836 2.25644 11.7755 2.59925 12.1273 3.06425C12.4791 3.52925 12.6712 4.08903 12.6712 4.66538C12.6712 5.24172 12.4791 5.8015 12.1273 6.2665C11.7755 6.7315 11.2836 7.07431 10.6667 7.2287" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Active Users
                      </div>
                      <div className="mock-stat-value">
                        {mockLoading ? (
                          <Skeleton variant="rounded" width={60} height={24} />
                        ) : (
                          mockMetrics.activeUsers
                        )}
                      </div>
                    </div>
                    <div className="mock-divider"></div>

                    {/* Avg Tests/Student */}
                    <div className="mock-stat">
                      <div className="mock-stat-label">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 4.6665V13.9998" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M2.00001 12C1.8232 12 1.65363 11.9298 1.52861 11.8047C1.40358 11.6797 1.33334 11.5101 1.33334 11.3333V2.66667C1.33334 2.48986 1.40358 2.32029 1.52861 2.19526C1.65363 2.07024 1.8232 2 2.00001 2H5.33334C6.04059 2 6.71886 2.28095 7.21896 2.78105C7.71906 3.28115 8.00001 3.95942 8.00001 4.66667C8.00001 3.95942 8.28096 3.28115 8.78106 2.78105C9.28116 2.28095 9.95943 2 10.6667 2H14C14.1768 2 14.3464 2.07024 14.4714 2.19526C14.5964 2.32029 14.6667 2.48986 14.6667 2.66667V11.3333C14.6667 11.5101 14.5964 11.6797 14.4714 11.8047C14.3464 11.9298 14.1768 12 14 12H10C9.46958 12 8.96087 12.2107 8.5858 12.5858C8.21072 12.9609 8.00001 13.4696 8.00001 14C8.00001 13.4696 7.7893 12.9609 7.41422 12.5858C7.03915 12.2107 6.53044 12 6.00001 12H2.00001Z" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Avg Tests/Student
                      </div>
                      <div className="mock-stat-value">
                        {mockLoading ? (
                          <Skeleton variant="rounded" width={60} height={24} />
                        ) : (
                          // mockMetrics.avgTestsPerStudent
                          (() => {
                            const submissions = mockMetrics.totalAttempts || 0;
                            const activeUsers = mockMetrics.activeUsers || 0;

                            if (submissions === 0 || activeUsers === 0) {
                              return "0.00";
                            }

                            return (submissions / activeUsers).toFixed(2);
                          })()
                        )}
                      </div>
                    </div>
                    <div className="mock-divider"></div>

                    {/* Total Attempts */}
                    <div className="mock-stat">
                      <div className="mock-stat-label">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M10 1.3335H4.00002C3.6464 1.3335 3.30726 1.47397 3.05721 1.72402C2.80716 1.97407 2.66669 2.31321 2.66669 2.66683V13.3335C2.66669 13.6871 2.80716 14.0263 3.05721 14.2763C3.30726 14.5264 3.6464 14.6668 4.00002 14.6668H12C12.3536 14.6668 12.6928 14.5264 12.9428 14.2763C13.1929 14.0263 13.3334 13.6871 13.3334 13.3335V4.66683L10 1.3335Z" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M9.33331 1.3335V4.00016C9.33331 4.35378 9.47379 4.69292 9.72384 4.94297C9.97389 5.19302 10.313 5.3335 10.6666 5.3335H13.3333" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M6.66665 6H5.33331" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10.6666 8.6665H5.33331" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10.6666 11.3335H5.33331" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Total Attempts
                      </div>
                      <div className="mock-stat-value">
                        {mockLoading ? (
                          <Skeleton variant="rounded" width={60} height={24} />
                        ) : (
                          mockMetrics.totalAttempts
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mock-divider-horizontal"></div>

                  <div className="mock-stats-row">
                    {/* Top User */}
                    <div className="mock-stat top-user">
                      <div className="mock-stat-label">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <g clipPath="url(#clip0_4575_4551)">
                            <path d="M10.318 8.59326L11.328 14.2773C11.3393 14.3442 11.3299 14.413 11.3011 14.4744C11.2722 14.5359 11.2253 14.5871 11.1666 14.6211C11.1079 14.6552 11.0402 14.6705 10.9725 14.6651C10.9049 14.6596 10.8405 14.6336 10.788 14.5906L8.40134 12.7993C8.28612 12.7132 8.14616 12.6667 8.00234 12.6667C7.85852 12.6667 7.71855 12.7132 7.60334 12.7993L5.21267 14.5899C5.16022 14.6329 5.09591 14.6588 5.02833 14.6643C4.96075 14.6698 4.89312 14.6545 4.83444 14.6205C4.77577 14.5865 4.72885 14.5355 4.69995 14.4742C4.67104 14.4128 4.66153 14.3441 4.67267 14.2773L5.682 8.59326" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8 9.3335C10.2091 9.3335 12 7.54264 12 5.3335C12 3.12436 10.2091 1.3335 8 1.3335C5.79086 1.3335 4 3.12436 4 5.3335C4 7.54264 5.79086 9.3335 8 9.3335Z" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          </g>
                          <defs>
                            <clipPath id="clip0_4575_4551">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        Top User
                      </div>
                      <div className="mock-stat-value-large">
                        {mockLoading ? (
                          <Skeleton variant="rounded" width={120} height={28} />
                        ) : (
                          mockMetrics.topUser.name
                        )}
                      </div>
                      <div className="mock-stat-sub">
                        {mockLoading ? (
                          <Skeleton variant="rounded" width={100} height={16} />
                        ) : (
                          `${mockMetrics.topUser.testsCompleted} Tests Completed`
                        )}
                      </div>
                    </div>
                    <div className="mock-divider"></div>

                    {/* Total Tests */}
                    <div className="mock-stat">
                      <div className="mock-stat-label">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M14.6667 4.6665L9.00001 10.3332L5.66668 6.99984L1.33334 11.3332" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10.6667 4.6665H14.6667V8.6665" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Total Tests
                      </div>
                      <div className="mock-stat-value">
                        {mockLoading ? (
                          <Skeleton variant="rounded" width={60} height={24} />
                        ) : (
                          `${mockMetrics.totalTestTaken ?? 0}/${mockMetrics.totalTests}`
                        )}
                      </div>
                    </div>
                    <div className="mock-divider"></div>

                    {/* Avg Score */}
                    <div className="mock-stat">
                      <div className="mock-stat-label">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <g clipPath="url(#clip0_4575_4573)">
                            <path d="M7.99998 14.6668C11.6819 14.6668 14.6666 11.6821 14.6666 8.00016C14.6666 4.31826 11.6819 1.3335 7.99998 1.3335C4.31808 1.3335 1.33331 4.31826 1.33331 8.00016C1.33331 11.6821 4.31808 14.6668 7.99998 14.6668Z" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8 4V8L10.6667 9.33333" stroke="#62748E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          </g>
                          <defs>
                            <clipPath id="clip0_4575_4573">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        Avg Score
                      </div>
                      <div className="mock-stat-value">
                        {mockLoading ? (
                          <Skeleton variant="rounded" width={60} height={24} />
                        ) : (
                          `${mockMetrics.avgScore}%`
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Top Students Leaderboard */}
            <div className="leaderboard-section">
              <SectionCard
                icon={
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <path d="M0 10C0 4.47715 4.47715 0 10 0H26C31.5228 0 36 4.47715 36 10V26C36 31.5228 31.5228 36 26 36H10C4.47715 36 0 31.5228 0 26V10Z" fill="#FCEBE8" />
                    <g clipPath="url(#clip0_4575_4583)">
                      <path d="M13 15.5002H11.75C11.1975 15.5002 10.6676 15.2807 10.2769 14.89C9.88617 14.4993 9.66667 13.9694 9.66667 13.4168C9.66667 12.8643 9.88617 12.3344 10.2769 11.9437C10.6676 11.553 11.1975 11.3335 11.75 11.3335H13" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M23 15.5002H24.25C24.8025 15.5002 25.3324 15.2807 25.7231 14.89C26.1138 14.4993 26.3333 13.9694 26.3333 13.4168C26.3333 12.8643 26.1138 12.3344 25.7231 11.9437C25.3324 11.553 24.8025 11.3335 24.25 11.3335H23" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M11.3333 26.3335H24.6667" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M16.3333 20.2168V22.1668C16.3333 22.6251 15.9417 22.9835 15.525 23.1751C14.5417 23.6251 13.8333 24.8668 13.8333 26.3335" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M19.6667 20.2168V22.1668C19.6667 22.6251 20.0583 22.9835 20.475 23.1751C21.4583 23.6251 22.1667 24.8668 22.1667 26.3335" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M23 9.6665H13V15.4998C13 16.8259 13.5268 18.0977 14.4645 19.0354C15.4021 19.9731 16.6739 20.4998 18 20.4998C19.3261 20.4998 20.5979 19.9731 21.5355 19.0354C22.4732 18.0977 23 16.8259 23 15.4998V9.6665Z" stroke="#F96343" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_4575_4583">
                        <rect width="20" height="20" fill="white" transform="translate(8 8)" />
                      </clipPath>
                    </defs>
                  </svg>
                }
                title="Top Students Leaderboard"
                headerRight={
                  <InputDropdown
                    options={batchOptions}
                    value={batchFilter}
                    onChange={(val) => setBatchFilter(val)}
                    placeholder="Select Batch"
                    searchable
                    className="inputdropdownbar"
                    icon={
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <g clipPath="url(#clip0_4575_4595)">
                          <path d="M5.83333 11.6667C5.83328 11.7751 5.86343 11.8813 5.92041 11.9736C5.97739 12.0658 6.05894 12.1403 6.15591 12.1887L7.32258 12.7727C7.41153 12.8165 7.51038 12.8375 7.60972 12.833C7.70906 12.8285 7.80568 12.7987 7.8901812.7464C7.97475 12.6941 8.04455 12.621 8.09294 12.5341C8.14133 12.4472 8.16671 12.3494 8.16667 12.25V8.16667C8.16679 7.87756 8.27426 7.59879 8.46824 7.38442L12.6817 2.72417C12.7572 2.64049 12.8068 2.53673 12.8246 2.42543C12.8424 2.31413 12.8276 2.20006 12.7819 2.09701C12.7363 1.99396 12.6617 1.90635 12.5673 1.84476C12.4729 1.78318 12.3627 1.75027 12.25 1.75H1.74999C1.63718 1.75004 1.5268 1.78279 1.43222 1.84429C1.33764 1.90578 1.26293 1.99338 1.21712 2.09648C1.17131 2.19957 1.15639 2.31374 1.17414 2.42514C1.1919 2.53655 1.24158 2.64042 1.31716 2.72417L5.53174 7.38442C5.72572 7.59879 5.8332 7.87756 5.83333 8.16667V11.6667Z" stroke="#45556C" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                        <defs>
                          <clipPath id="clip0_4575_4595">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    }
                  />
                }
              >
                {leaderboardLoading ? (
                  <TopStudentsLeaderSkeleton />
                ) : (
                  <Table columns={columns} data={topStudents} hoverable={false} />
                )}
              </SectionCard>
            </div>
          </div>
        ) : (
          <BatchOverview search={batchSearch} isRefreshRef={isRefreshRef} refreshTrigger={refreshTrigger} setIsCached={setIsCached}/>
        )}
      </div>
    </div>
  );
}